'use client';

import {cn} from '@/lib/utils';
import Autoplay from 'embla-carousel-autoplay';
import {useEffect, useState} from 'react';
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious
} from '../ui/carousel';

export default function SliderBase(Prop: {
  items: any;
  options?: any;
  template: (sliderItem: any) => any;
  scrollToIndex?: any;
}) {
  const sliderSetting = Prop.options || {};
  // sliderSetting
  const sliderItems = Prop?.items || [];

  // carousel
  const [carouselApi, setCarouselApi] = useState<CarouselApi | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);

  const scrollToIndex = (index: number) => {
    carouselApi?.scrollTo(index);
  };

  const pauseSlider = () => {
    if (sliderItems.length) {
      carouselApi?.plugins()?.autoplay?.stop();
      setIsPaused(true);
    }
  };

  const resumeSlider = () => {
    if (sliderItems.length && !sliderSetting.noAutoPlay) {
      carouselApi?.plugins()?.autoplay?.play();
      setIsPaused(false);
    }
  };
  useEffect(() => {
    if (!carouselApi) return;

    const onSelect = () => {
      setCurrentIndex(carouselApi.selectedScrollSnap());
    };

    const onAutoplayPlay = () => {
      setIsPaused(false);
    };

    const onAutoplayStop = () => {
      setIsPaused(true);
    };

    carouselApi.on('select', onSelect);
    carouselApi.on('autoplay:play', onAutoplayPlay);
    carouselApi.on('autoplay:stop', onAutoplayStop);

    return () => {
      carouselApi.off('select', onSelect);
      carouselApi.off('autoplay:play', onAutoplayPlay);
      carouselApi.off('autoplay:stop', onAutoplayStop);
    };
  }, [carouselApi]);

  const formTemplate = (sliderItem: any) => {
    return Prop?.template(sliderItem);
  };

  useEffect(() => {
    if (sliderSetting.noAutoPlay) {
      if (sliderItems.length) {
        carouselApi?.plugins()?.autoplay?.stop();
      }
    }
  }, []);

  const sliderItemLength = sliderItems.length;
  const carouselPlugins = [];
  if (sliderItemLength > 1) {
    carouselPlugins.push(Autoplay({delay: sliderSetting.interval ?? 10000}));
  }

  if (Prop.scrollToIndex) {
    Prop.scrollToIndex(carouselApi);
  }

  useEffect(() => {
    if (!carouselApi) return;

    const updateScrollState = () => {
      setIsDisabled(
        !carouselApi.canScrollPrev() || !carouselApi.canScrollNext()
      );
    };

    updateScrollState();
    carouselApi.on('select', updateScrollState);
    carouselApi.on('reInit', updateScrollState);

    return () => {
      carouselApi.off('select', updateScrollState);
      carouselApi.off('reInit', updateScrollState);
    };
  }, [carouselApi]);

  return (
    <>
      {sliderItems.length > 0 && (
        <div
          className="questrial text-[18px] w-full"
          // onMouseEnter={pauseSlider}
          // onMouseLeave={resumeSlider}
        >
          <Carousel
            setApi={setCarouselApi}
            opts={{
              loop: true,
              active: sliderItems.length > 1
            }}
            className="w-full relative group"
            plugins={carouselPlugins}
          >
            <CarouselContent>
              {sliderItems.map((sliderItem: any, index: number) => (
                <CarouselItem
                  key={index}
                  className={cn(Prop?.options?.basis || 'md:basis-1/3')}
                >
                  {formTemplate(sliderItem)}
                </CarouselItem>
              ))}
            </CarouselContent>
            {sliderItems.length > 1 && (
              <div
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <button
                  title="pause"
                  className={cn(
                    'absolute opacity-50 md:opacity-30 group-hover:opacity-60 bg-[#00000050] text-white border-none rounded-full w-[24px] h-[24px]',
                    !sliderSetting?.fullWidth && 'md:right-[-50px]',
                    sliderSetting?.pauseAt ?? 'top-5 left-10'
                  )}
                  onClick={(e) => {
                    isPaused ? resumeSlider() : pauseSlider();
                    e.stopPropagation();
                  }}
                >
                  {isPaused ? (
                    <span className="material-symbols">play_circle</span>
                  ) : (
                    <span className="material-symbols">pause_circle</span>
                  )}
                </button>
                <CarouselNext
                  className={cn(
                    'absolute right-10 opacity-50 md:opacity-30 group-hover:opacity-60 bg-black text-white border-[0px] focus-visible:border-[2px] focus-visible:border-white ',
                    !sliderSetting?.fullWidth && 'md:right-[-50px]',
                    isDisabled && 'hidden'
                  )}
                />
                <CarouselPrevious
                  className={cn(
                    'absolute left-10 opacity-50 md:opacity-30 group-hover:opacity-60 bg-[#000000] text-white border-[0px] focus-visible:border-[2px] focus-visible:border-white',
                    !sliderSetting?.fullWidth && 'md:left-[-50px]',
                    isDisabled && 'hidden'
                  )}
                />
              </div>
            )}
          </Carousel>
          {sliderSetting.withDots && sliderItems.length > 1 && (
            <div className=" flex justify-center space-x-2 z-20 my-4">
              {Array.from({length: sliderItems.length}).map((_, index) => (
                <div
                  key={'dot_' + index}
                  onClick={() => scrollToIndex(index)}
                  className={cn(
                    'w-3 h-3 rounded-full bg-black cursor-pointer',
                    {' opacity-20': currentIndex !== index}
                  )}
                ></div>
              ))}
            </div>
          )}
          {sliderSetting.withImgs && sliderItems.length > 1 && (
            <div className=" flex justify-center space-x-2 z-20 my-4">
              {sliderItems.map((sliderItem: any, index: number) => (
                <div
                  key={'dot_' + index}
                  onClick={() => scrollToIndex(index)}
                  className={cn('cursor-pointer w-[20%]', {
                    'border border-[#E12726]': currentIndex == index
                  })}
                >
                  <img
                    src={sliderItem.image}
                    alt={sliderItem.caption}
                    className="aspect-square  object-contain"
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}
